import * as XLSX from 'xlsx-js-style';
import { alertService } from '../../../../_services/alert.service';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

/**
 * Export hierarchical table data to Excel
 * @param {Array} data - The table data source
 * @param {string} activeTab - Current active tab ('clusters' or 'arrays')
 * @param {Object} options - Additional options (selectedDepartment, etc.)
 * @param {Object} signedobj - Signed in user details for geenrated by field
 */
export const exportToExcel = (data, activeTab, options = {}, signedobj) => {
     
    try {
        const isRTL = i18n.language === 'he';
        // Filter out unassigned rows
        const exportData = data.filter(item => {
            const key = String(item.key || '');
            return !key.includes('unassigned-cluster-') && 
                   !key.includes('unassigned-array-');
        });

        // Create flattened data for Excel
        const excelData = [];
                const currentDateTime = new Date().toLocaleString('he-IL');
        const selectedDepartment = options.selectedDepartment;
        const userDetails = (signedobj && signedobj.signinDetails ? signedobj.signinDetails.userDto : null);
        
        // Create header rows with translated labels
        excelData.push(['', '', i18n.t('REPORT_TYPE'), activeTab === 'clusters' ? i18n.t('CLUSTERS_REPORT') : i18n.t('ARRAYS_REPORT'), '', '', '']);
        excelData.push(['', '', i18n.t('REPORT_DATETIME'), currentDateTime, '', '', '']);
        excelData.push(['', '', i18n.t('DEPARTMENT'), selectedDepartment, '', '', '']);
        excelData.push(['', '', i18n.t('GENERATED_BY'), userDetails?.fName + ' ' + userDetails?.lName, '', '', '']);
        excelData.push([]); // Empty row 1
        excelData.push([]); // Empty row 2  
        excelData.push([]); // Empty row 3
        
        // Add data rows with vertical 2-column format for main tables
        exportData.forEach(parentItem => {
            // Skip unassigned rows
            const key = String(parentItem.key || '');
            if (key.includes('unassigned')) return;
            
            // Add main cluster/array data as vertical label-value pairs
            const storeCount = parentItem.stores ? parentItem.stores.length : 0;
            
            // Add each field as a label-value pair
            const codeValue = parentItem.code || '';
            const cleanCode = typeof codeValue === 'string' ? codeValue.replace('Code: #', '') : codeValue;
            excelData.push([activeTab === 'clusters' ? i18n.t('CLUSTER_CODE') : i18n.t('ARRAY_CODE'), cleanCode]);
            excelData.push([activeTab === 'clusters' ? i18n.t('CLUSTER_NAME') : i18n.t('ARRAY_NAME'), activeTab === 'clusters' ? parentItem.clusterName : parentItem.arrayName]);
            excelData.push([i18n.t('ASSIGNED_STORE_COUNT'), storeCount.toString()]);
            excelData.push([i18n.t('ARRAY_WIDTH'), parentItem.width || '0']);
            excelData.push([i18n.t('ARRAY_FIELD_COUNT'), parentItem.fields || '0']);
            if (activeTab === 'arrays') {
                excelData.push([i18n.t('ARRAY_SHELF_COUNT'), parentItem.s6 || '0']);
            }
            if (activeTab === 'clusters') {
                excelData.push([i18n.t('LINKED_ARRAY_NAME'), parentItem.linkArrayName || i18n.t('NONE')]);
            }
            excelData.push([i18n.t('DESCRIPTION'), parentItem.description || '']);
            
            // Add empty row after main data
            excelData.push([]);
            
            // Add stores table if there are stores
            if (parentItem.stores && parentItem.stores.length > 0) {
                // Add stores table headers based on active tab
                const storeHeaders = activeTab === 'clusters' ? [
                    i18n.t('STORE_CODE'),
                    i18n.t('STORE_NAME'), 
                    i18n.t('REGION_NAME'), 
                    i18n.t('TAG_NAMES'),
                    i18n.t('WIDTH'),
                    i18n.t('FIELDS'), 
                    i18n.t('LINKED_ARRAY_NAME')
                ] : [
                    i18n.t('STORE_CODE'),
                    i18n.t('STORE_NAME'), 
                    i18n.t('REGION_NAME'), 
                    i18n.t('TAGS'),
                    i18n.t('LINKED_CLUSTER_NAME')
                ];
                excelData.push(storeHeaders);
                
                // Add store rows based on active tab
                parentItem.stores.forEach(store => {
                    const storeRow = activeTab === 'clusters' ? [
                        store.storeCode || '',
                        store.storeName || '',
                        store.storeRegion?.regionName || '',
                        store.tagNames ? store.tagNames.join(', ') : '',
                        store.width || '0',
                        store.fields || '0',
                        store.fieldArray.fieldArrayName || i18n.t('NONE')
                    ] : [
                        store.storeCode || '',
                        store.storeName || '',
                        store.storeRegion?.regionName || '',
                        store.tagNames ? store.tagNames.join(', ') : '',
                        store.cluster?.clusterName || i18n.t('NONE')
                    ];
                    excelData.push(storeRow);
                });
                
                // Add 2 empty rows after stores table for separation
                excelData.push([]);
                excelData.push([]);
            } else {
                // Add empty table message row for clusters/arrays with no stores
                const emptyStoresRow = activeTab === 'clusters' ? [
                    i18n.t('NO_STORES'),
                    '', '', '', '', '', ''
                ] : [
                    '', '', i18n.t('NO_STORES_AVAILABLE'),
                    '', ''
                ];
                excelData.push(emptyStoresRow);
                
                // Add 2 empty rows after empty message for separation
                excelData.push([]);
                excelData.push([]);
            }
        });
        
        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(excelData);
        
        // Set column widths based on active tab
        const colWidths = activeTab === 'clusters' ? [
            { wch: 25 }, // Labels or Store Code
            { wch: 30 }, // Values or Store Name
            { wch: 15 }, // Region Name
            { wch: 20 }, // Tag Names
            { wch: 8 },  // Width
            { wch: 8 },  // Fields
            { wch: 20 }  // Linked Array Name
        ] : [
            { wch: 25 }, // Labels or Store Code
            { wch: 30 }, // Values or Store Name
            { wch: 15 }, // Region Name
            { wch: 20 }, // Tag Names
            { wch: 20 }  // Linked Cluster Name
        ];
        ws['!cols'] = colWidths;
        
        // Apply styles to header rows
        // Header metadata styling - left aligned text, centered positioning via merges
        const metadataLabelStyle = {
            font: { bold: true, size: 12 },
            fill: { fgColor: { rgb: "E6E6E6" } },
            alignment: { horizontal: isRTL ? "right" : "left", vertical: "center", readingOrder: isRTL ? 2 : 0 },
            border: {
                top: { style: "thin", color: { rgb: "CCCCCC" } },
                bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                left: { style: "thin", color: { rgb: "CCCCCC" } },
                right: { style: "thin", color: { rgb: "CCCCCC" } }
            }
        };
        
        const metadataValueStyle = {
            font: { size: 12 },
            fill: { fgColor: { rgb: "F9F9F9" } },
            alignment: { horizontal: isRTL ? "right" : "left", vertical: "center", readingOrder: isRTL ? 2 : 0, wrapText: true },
            border: {
                top: { style: "thin", color: { rgb: "CCCCCC" } },
                bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                left: { style: "thin", color: { rgb: "CCCCCC" } },
                right: { style: "thin", color: { rgb: "CCCCCC" } }
            }
        };
        
        // Main table headers (clusters/arrays)
        const mainHeaderStyle = {
            font: { bold: true, size: 12 },
            fill: { fgColor: { rgb: "4A90E2" } },
            alignment: { horizontal: isRTL ? "right" : "left", vertical: "center", readingOrder: isRTL ? 2 : 0 },
            border: {
                top: { style: "medium", color: { rgb: "2E5C8A" } },
                bottom: { style: "medium", color: { rgb: "2E5C8A" } },
                left: { style: "medium", color: { rgb: "2E5C8A" } },
                right: { style: "medium", color: { rgb: "2E5C8A" } }
            }
        };
        
        // Main table labels (left column) - darker gray background
        const mainLabelStyle = {
            font: { bold: true, size: 11 },
            fill: { fgColor: { rgb: "D0D0D0" } },
            alignment: { horizontal: isRTL ? "right" : "left", vertical: "center", readingOrder: isRTL ? 2 : 0 },
            border: {
                top: { style: "thin", color: { rgb: "CCCCCC" } },
                bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                left: { style: "thin", color: { rgb: "CCCCCC" } },
                right: { style: "thin", color: { rgb: "CCCCCC" } }
            }
        };
        
        // Main table values (right column) - no background color
        const mainValueStyle = {
            font: { size: 11 },
            alignment: { horizontal: isRTL ? "right" : "left", vertical: "center", readingOrder: isRTL ? 2 : 0, wrapText: true },
            border: {
                top: { style: "thin", color: { rgb: "CCCCCC" } },
                bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                left: { style: "thin", color: { rgb: "CCCCCC" } },
                right: { style: "thin", color: { rgb: "CCCCCC" } }
            }
        };
        
        // Store table headers
        const storeHeaderStyle = {
            font: { bold: true, size: 10 },
            fill: { fgColor: { rgb: "EEEEEE" } },
            alignment: { horizontal: isRTL ? "right" : "left", vertical: "center", readingOrder: isRTL ? 2 : 0 },
            border: {
                top: { style: "thin", color: { rgb: "AAAAAA" } },
                bottom: { style: "thin", color: { rgb: "AAAAAA" } },
                left: { style: "thin", color: { rgb: "AAAAAA" } },
                right: { style: "thin", color: { rgb: "AAAAAA" } }
            }
        };
        
        // Store table data rows (no background color)
        const storeDataStyle = {
            font: { size: 10 },
            alignment: { horizontal: isRTL ? "right" : "left", vertical: "center", readingOrder: isRTL ? 2 : 0, wrapText: true },
            border: {
                top: { style: "thin", color: { rgb: "EEEEEE" } },
                bottom: { style: "thin", color: { rgb: "EEEEEE" } },
                left: { style: "thin", color: { rgb: "EEEEEE" } },
                right: { style: "thin", color: { rgb: "EEEEEE" } }
            }
        };
        
        // Empty table message style
        const emptyTableStyle = {
            font: { size: 10 },
            fill: { fgColor: { rgb: "EEEEEE" } },
            alignment: { horizontal: isRTL ? "right" : "left", vertical: "center", readingOrder: isRTL ? 2 : 0, wrapText: true },
            border: {
                top: { style: "thin", color: { rgb: "AAAAAA" } },
                bottom: { style: "thin", color: { rgb: "AAAAAA" } },
                left: { style: "thin", color: { rgb: "AAAAAA" } },
                right: { style: "thin", color: { rgb: "AAAAAA" } }
            }
        };
        
        // Style metadata rows - adjacent center columns
        for (let row = 1; row <= 4; row++) {
            const labelCellRef = XLSX.utils.encode_cell({ r: row - 1, c: 2 });
            if (!ws[labelCellRef]) ws[labelCellRef] = { v: '', t: 's' };
            ws[labelCellRef].s = metadataLabelStyle;
            
            const valueCellRef = XLSX.utils.encode_cell({ r: row - 1, c: 3 });
            if (!ws[valueCellRef]) ws[valueCellRef] = { v: '', t: 's' };
            ws[valueCellRef].s = metadataValueStyle;
        }
        
        if (!ws['!merges']) ws['!merges'] = [];
        

        const mainLabelKeys = [
            i18n.t('CLUSTER_NAME'),
            i18n.t('ARRAY_NAME'),
            i18n.t('ASSIGNED_STORE_COUNT'),
            i18n.t('CLUSTER_CODE'),
            i18n.t('ARRAY_CODE'),
            i18n.t('ARRAY_WIDTH'),
            i18n.t('ARRAY_FIELD_COUNT'),
            i18n.t('ARRAY_SHELF_COUNT'),
            i18n.t('LINKED_ARRAY_NAME'),
            i18n.t('LINKED_CLUSTER_NAME'),
            i18n.t('DESCRIPTION')
        ];
        
        // Apply styles to dynamic content
        for (let rowIndex = 0; rowIndex < excelData.length; rowIndex++) {
            const row = excelData[rowIndex];
            
            if (rowIndex < 5) continue;
            
            // Check if this is a main table label-value pair row
            if (row.length === 2 && mainLabelKeys.includes(row[0])) {
                const labelCellRef = XLSX.utils.encode_cell({ r: rowIndex, c: 0 });
                if (!ws[labelCellRef]) ws[labelCellRef] = { v: '', t: 's' };
                ws[labelCellRef].s = mainLabelStyle;
                
                const valueCellRef = XLSX.utils.encode_cell({ r: rowIndex, c: 1 });
                if (!ws[valueCellRef]) ws[valueCellRef] = { v: '', t: 's' };
                ws[valueCellRef].s = mainValueStyle;
            }
            // Check if this is a store header row
            else if (row.includes(i18n.t('STORE_CODE')) && row[0] === i18n.t('STORE_CODE')) {
                for (let colIndex = 0; colIndex < row.length; colIndex++) {
                    const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
                    if (!ws[cellRef]) ws[cellRef] = { v: '', t: 's' };
                    ws[cellRef].s = storeHeaderStyle;
                }
            }
            // Check if this is a store data row
            else if ((row.length === 7 || row.length === 5) && row[0] !== '' && !row.includes(i18n.t('STORE_CODE')) && row[0] !== i18n.t('NO_STORES')) {
                for (let colIndex = 0; colIndex < row.length; colIndex++) {
                    const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
                    if (!ws[cellRef]) ws[cellRef] = { v: '', t: 's' };
                    ws[cellRef].s = storeDataStyle;
                }
            }
            // Check if this is an empty table message row
            else if ((row.length === 7 || row.length === 5) && (row[0] === i18n.t('NO_STORES') || row.includes(i18n.t('NO_STORES_AVAILABLE')))) {
                for (let colIndex = 0; colIndex < row.length; colIndex++) {
                    const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
                    if (!ws[cellRef]) ws[cellRef] = { v: '', t: 's' };
                    ws[cellRef].s = emptyTableStyle;
                }
            }
        }
        
        // Add worksheet to workbook (translated sheet name)
        const sheetName = activeTab === 'clusters' ? i18n.t('CLUSTERS_REPORT') : i18n.t('ARRAYS_REPORT');
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        
        // Generate filename
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const filename = options.filename || `${sheetName.replace(' ', '_')}_${timestamp}.xlsx`;
        
        // Save file
        XLSX.writeFile(wb, filename);
        
        alertService.success(i18n.t('EXCEL_EXPORT_COMPLETE')); 
        
        return { success: true, filename };
    } catch (error) {
        console.error('Excel export failed:', error);
        
        if (options.showErrorMessage !== false) {
            alertService.error(i18n.t('EXCEL_EXPORT_ERROR')); 
        }
        return { success: false, error: error.message };
    }
};